import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { devicesApi, type DeviceLoginRow } from '@/api/devices';
import { Card, CardTitle } from '@/components/ui/Card';
import {
  DEVICE_LOG_SINCE,
  browserKey,
  deviceTypeKey,
  formatLoginAt,
  isHiddenValue,
  osKey,
} from '@/lib/deviceLabels';
import { humanizeError } from '@/lib/errors';

/**
 * Откуда ученик входил: устройство · IP · заходов · последний раз (D41, договор
 * `docs/CONTRACTS/devices.md`). Стоит в обеих карточках ученика — админской и
 * кураторской: ручку сервер открыл и куратору намеренно.
 *
 * ⚠️ ЭТО ВХОДЫ, А НЕ ЖИВЫЕ СЕССИИ. Страница «Устройства» в админке показывает
 * другое — сессии по номеру приложения, которые гаснут при выходе.
 *
 * ⚠️ ОДИН АДРЕС — НЕ ОДИН ЧЕЛОВЕК. За адресом мобильного оператора или школьного
 * Wi-Fi сидит много людей, а мобильный интернет меняет адрес по нескольку раз на
 * дню. Вывод «раздал пароль» по совпадению адреса делать нельзя — поэтому в
 * таблице нет ни подсветки, ни предупреждений: только цифры.
 */
export function StudentDevicesCard({ studentId }: { studentId: string }) {
  const { t, i18n } = useTranslation();
  const q = useQuery({
    queryKey: ['admin', 'device-logins', studentId],
    queryFn: () => devicesApi.logins(studentId),
    enabled: !!studentId,
  });

  const items = q.data?.items ?? [];
  const total = q.data?.total ?? 0;
  const since = new Date(DEVICE_LOG_SINCE).toLocaleDateString(i18n.language.startsWith('en') ? 'en-GB' : 'ru-RU', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });

  return (
    <Card className="space-y-3" data-student-devices>
      <CardTitle>{t('studentDevices.title')}</CardTitle>

      {q.isLoading ? <p className="text-sm text-on-surface-variant">{t('common.loading')}</p> : null}

      {q.isError ? (
        <p className="text-sm text-error" role="alert" data-student-devices-error>
          {humanizeError(q.error)}
        </p>
      ) : null}

      {q.data && !items.length ? (
        // ⚠️ «С такого-то числа не входил», а НЕ «никогда не входил»: учёт начался
        // с выката, прежние входы не восстановлены.
        <p className="text-sm text-on-surface-variant" data-student-devices-empty>
          {t('studentDevices.emptySince', { date: since })}
        </p>
      ) : null}

      {items.length ? (
        <>
          {/* Широкий экран — таблица. */}
          <div className="hidden overflow-x-auto md:block">
            <table className="w-full text-left text-sm">
              <thead className="border-b border-outline-variant/40 text-xs uppercase tracking-label text-on-surface-variant">
                <tr>
                  <th className="pb-2 pr-3">{t('studentDevices.colDevice')}</th>
                  <th className="pb-2 pr-3">{t('studentDevices.colIp')}</th>
                  <th className="pb-2 pr-3 text-right">{t('studentDevices.colLogins')}</th>
                  <th className="pb-2 text-right">{t('studentDevices.colLast')}</th>
                </tr>
              </thead>
              <tbody>
                {items.map((row) => (
                  <tr key={rowKey(row)} className="border-b border-outline-variant/20 last:border-0">
                    <td className="py-2 pr-3 text-on-surface">{deviceText(row, t)}</td>
                    <td className="py-2 pr-3 font-mono text-xs text-on-surface-variant">{row.ip_address || '—'}</td>
                    <td className="py-2 pr-3 text-right tabular-nums">{row.logins}</td>
                    <td className="py-2 text-right tabular-nums text-on-surface-variant">
                      {formatLoginAt(row.last_login_at, i18n.language) || '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Телефон — карточки: четыре колонки в 390 px не помещаются, а
              горизонтальная прокрутка внутри карточки ученика теряется. */}
          <ul className="space-y-2 md:hidden">
            {items.map((row) => (
              <li key={rowKey(row)} className="rounded-xl bg-surface-container-low p-3 text-sm">
                <p className="font-medium text-on-surface">{deviceText(row, t)}</p>
                <p className="font-mono text-xs text-on-surface-variant">{row.ip_address || '—'}</p>
                <p className="mt-1 text-xs text-on-surface-variant">
                  {t('studentDevices.loginsShort', { count: row.logins })} ·{' '}
                  {formatLoginAt(row.last_login_at, i18n.language) || '—'}
                </p>
              </li>
            ))}
          </ul>

          {total > items.length ? (
            <p className="text-xs text-on-surface-variant" data-student-devices-more>
              {t('studentDevices.shownOf', { shown: items.length, total })}
            </p>
          ) : null}
        </>
      ) : null}
    </Card>
  );
}

/** Ключ строки — пара «устройство + адрес», она же и есть строка на сервере. */
function rowKey(row: DeviceLoginRow): string {
  return `${row.device_type}|${row.os}|${row.browser}|${row.ip_address}`;
}

/**
 * «Телефон · iOS · Safari». Незнакомое значение показывается как есть, `other` у
 * системы и браузера не показывается вовсе.
 *
 * ⚠️ ИМЯ НЕ НАЧИНАЕТСЯ С `use`. Сперва функция называлась `useDeviceText`, и
 * линтер справедливо считал её хуком: вызов внутри `map` — это условный вызов
 * хука, то есть ошибка `react-hooks/rules-of-hooks`, роняющая проверку
 * независимо от порога предупреждений.
 */
function deviceText(row: DeviceLoginRow, t: (key: string) => string): string {
  const parts: string[] = [];
  const typeKey = deviceTypeKey(row.device_type);
  parts.push(typeKey ? t(typeKey) : row.device_type);

  const os = osKey(row.os);
  if (os) parts.push(t(os));
  else if (!isHiddenValue(row.os)) parts.push(row.os);

  const browser = browserKey(row.browser);
  if (browser) parts.push(t(browser));
  else if (!isHiddenValue(row.browser)) parts.push(row.browser);

  return parts.join(' · ');
}
